import { create } from "zustand";
import type { ChecklistData } from "@/react/types";
import {
  type CommunityAccess,
  type StepKey,
  communityAccessOptions,
} from "@/react/types/Checklist";

interface ChecklistState {
  checklist: ChecklistData | null;
  communityAccess: CommunityAccess;
  expandedSections: Record<string, boolean>;
  focusedStepKey: StepKey | null;
  setChecklist: (checklist: ChecklistData | null) => void;
  setCommunityAccess: (access: CommunityAccess) => void;
  setExpandedSections: (expanded: Record<string, boolean>) => void;
  setFocusedStepKey: (stepKey: StepKey | null) => void;
  toggleSectionExpansion: (
    sectionTitle: string,
    sections?: Array<{ title: string; steps: Array<{ focus: boolean }> }>,
  ) => void;
}

export const useChecklistStore = create<ChecklistState>(set => ({
  expandedSections: {},
  focusedStepKey: null,
  checklist: null,
  communityAccess: communityAccessOptions.public,

  setChecklist: (checklist: ChecklistData | null) => {
    set({ checklist });
  },

  setFocusedStepKey: (stepKey: StepKey | null) => {
    set({ focusedStepKey: stepKey });
  },

  setExpandedSections: (expanded: Record<string, boolean>) => {
    set({ expandedSections: expanded });
  },

  setCommunityAccess: (access: CommunityAccess) => {
    set({ communityAccess: access });
  },

  toggleSectionExpansion: (
    sectionTitle: string,
    sections?: Array<{ title: string; steps: Array<{ focus: boolean }> }>,
  ) => {
    set(state => {
      const isCurrentlyExpanded = !!state.expandedSections[sectionTitle];
      const newExpandedSections = { ...state.expandedSections };

      if (isCurrentlyExpanded) {
        const hasFocusedStep = sections?.some(
          section =>
            section.title === sectionTitle &&
            section.steps.some(step => step.focus),
        );
        if (hasFocusedStep) {
          return {
            ...state,
            expandedSections: { ...newExpandedSections, [sectionTitle]: false },
            focusedStepKey: null,
          };
        }
        newExpandedSections[sectionTitle] = false;
      } else {
        newExpandedSections[sectionTitle] = true;
      }
      return { ...state, expandedSections: newExpandedSections };
    });
  },
}));
